import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { revenueClient } from '../services/revenueClient';
import './RevenueDashboard.css';

const RevenueDashboard = () => {
  const { user } = useAuth();
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    fetchRevenueData();
  }, [selectedMonth]);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      const response = await revenueClient.getRevenueSummary(selectedMonth);
      if (response.success) {
        setRevenueData(response);
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError('Failed to fetch revenue data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="revenue-dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading revenue data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="revenue-dashboard">
        <div className="error-message">
          <h3>Error Loading Revenue Data</h3>
          <p>{error}</p>
          <button onClick={fetchRevenueData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!revenueData) {
    return (
      <div className="revenue-dashboard">
        <div className="no-data">
          <h3>No Revenue Data Available</h3>
          <p>Start trading to see your revenue information here.</p>
        </div>
      </div>
    );
  }

  const { account, monthly_stats, recent_transactions } = revenueData;

  return (
    <div className="revenue-dashboard">
      <div className="dashboard-header">
        <h2>💰 Revenue Dashboard</h2>
        <div className="month-selector">
          <label htmlFor="month-select">Select Month:</label>
          <input
            type="month"
            id="month-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="month-input"
          />
        </div>
      </div>

      {/* Account Overview */}
      <div className="account-overview">
        <div className="overview-card">
          <h3>Account Balance</h3>
          <div className="balance-amount">
            {formatCurrency(account.account_balance)}
          </div>
          <div className="balance-label">Available Balance</div>
        </div>

        <div className="overview-card">
          <h3>Total Profit</h3>
          <div className="profit-amount">
            {formatCurrency(account.total_profit)}
          </div>
          <div className="profit-label">Lifetime Earnings</div>
        </div>

        <div className="overview-card">
          <h3>Platform Fees</h3>
          <div className="fees-amount">
            {formatCurrency(account.total_fees_paid)}
          </div>
          <div className="fees-label">Total Fees Paid</div>
        </div>

        <div className="overview-card pending">
          <h3>Pending Fees</h3>
          <div className="pending-amount">
            {formatCurrency(account.pending_fees)}
          </div>
          <div className="pending-label">Awaiting Payment</div>
        </div>
      </div>

      {/* Monthly Statistics */}
      {monthly_stats && (
        <div className="monthly-stats">
          <h3>📊 Monthly Performance - {monthly_stats.month}</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{monthly_stats.total_trades}</div>
              <div className="stat-label">Total Trades</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{formatPercentage(monthly_stats.win_rate)}</div>
              <div className="stat-label">Win Rate</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{formatCurrency(monthly_stats.total_profit)}</div>
              <div className="stat-label">Gross Profit</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{formatCurrency(monthly_stats.total_fees)}</div>
              <div className="stat-label">Platform Fees</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{formatCurrency(monthly_stats.net_profit)}</div>
              <div className="stat-label">Net Profit</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{monthly_stats.winning_trades}</div>
              <div className="stat-label">Winning Trades</div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      <div className="recent-transactions">
        <h3>📈 Recent Transactions</h3>
        <div className="transactions-table">
          <div className="table-header">
            <div>Date</div>
            <div>Symbol</div>
            <div>Profit</div>
            <div>Platform Fee</div>
            <div>Net Profit</div>
            <div>Status</div>
          </div>
          {recent_transactions.map((transaction) => (
            <div key={transaction.id} className="table-row">
              <div>{new Date(transaction.created_at).toLocaleDateString()}</div>
              <div>{transaction.symbol || 'N/A'}</div>
              <div className={transaction.gross_profit >= 0 ? 'profit' : 'loss'}>
                {formatCurrency(transaction.gross_profit)}
              </div>
              <div className="fee">
                {formatCurrency(transaction.platform_fee)}
              </div>
              <div className={transaction.net_profit >= 0 ? 'profit' : 'loss'}>
                {formatCurrency(transaction.net_profit)}
              </div>
              <div className={`status ${transaction.status}`}>
                {transaction.status}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Settings */}
      <div className="payment-settings">
        <h3>💳 Payment Settings</h3>
        <div className="settings-card">
          <div className="setting-item">
            <label>Auto Pay</label>
            <div className="setting-value">
              {account.auto_pay_enabled ? '✅ Enabled' : '❌ Disabled'}
            </div>
          </div>
          <div className="setting-item">
            <label>Fee Rate</label>
            <div className="setting-value">
              {formatPercentage(account.fee_rate * 100)}
            </div>
          </div>
          <div className="setting-item">
            <label>Minimum Threshold</label>
            <div className="setting-value">
              {formatCurrency(account.minimum_threshold)}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <button className="btn btn-primary" onClick={fetchRevenueData}>
          Refresh Data
        </button>
        <button className="btn btn-secondary">
          Download Report
        </button>
        <button className="btn btn-warning">
          Setup Payment
        </button>
      </div>
    </div>
  );
};

export default RevenueDashboard;
